import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'
import { To, useNavigate } from 'react-router-dom'
import { Button as ButtonUI } from '../ui/button'

/**
 * O que o `ui/button` não dá para o botão redondo de ícone: o padding por
 * tamanho do MUI e a paleta por `color`. O `primary` é o padrão porque era o
 * `defaultProps` de `MuiIconButton` no `theme.ts`.
 */
const estiloMui = cva(
    [
        'relative rounded-full p-0 flex items-center justify-center',
        // O `ghost` do `ui/button` troca a cor do texto no hover, o que apagaria
        // a cor do ícone — aqui só o fundo reage.
        'hover:bg-[color-mix(in_oklab,currentColor_10%,transparent)] hover:text-current',
        'disabled:text-muted-foreground',
        'aria-disabled:pointer-events-none aria-disabled:opacity-50',
    ],
    {
        variants: {
            /**
             * Caixa de tamanho fixo, não padding em volta do ícone: é o que
             * mantém todos os botões redondos iguais mesmo com ícones de
             * tamanhos diferentes. O ícone só recebe um tamanho padrão quando
             * o call-site não define o dele — um `size-*` próprio vence.
             */
            size: {
                small: "size-8 [&_svg:not([class*='size-'])]:size-5",
                medium: "size-10 [&_svg:not([class*='size-'])]:size-7",
                large: "size-12 [&_svg:not([class*='size-'])]:size-9",
            },
            color: {
                primary: 'text-primary',
                secondary: 'text-secondary',
                error: 'text-error',
                warning: 'text-warning',
                success: 'text-success',
                info: 'text-info',
                inherit: 'text-inherit',
                default: 'text-foreground',
            },
            /** Compensa a caixa quando o botão encosta na borda do container. */
            edge: {
                start: '-ml-2',
                end: '-mr-2',
            },
        },
        defaultVariants: { size: 'medium', color: 'primary' },
    },
)

type Props = Omit<React.ComponentProps<'button'>, 'color'> &
    VariantProps<typeof estiloMui> & {
        /** Navega por rota interna ao clicar. Para URL externa, use `IconLink`. */
        href?: To
        /** Repassa as classes ao filho, em vez de renderizar outro botão. */
        asChild?: boolean
    }

const IconButton = React.forwardRef<HTMLButtonElement, Props>(
    (
        {
            href,
            size = 'medium',
            color,
            edge,
            className,
            onClick,
            type = 'button',
            asChild,
            ...rest
        },
        ref,
    ) => {
        const navigate = useNavigate()

        return (
            <ButtonUI
                {...rest}
                ref={ref}
                type={type}
                asChild={asChild}
                variant="ghost"
                className={cn(estiloMui({ size, color, edge }), className)}
                onClick={e => {
                    onClick?.(e)
                    if (href) navigate(href)
                }}
            />
        )
    },
)

IconButton.displayName = 'IconButton'

export default IconButton
